import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import api from '@/lib/api'
import {
  Loader2, Play, Download, CheckCircle2, AlertTriangle, ChevronRight,
} from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ScatterChart, Scatter, ZAxis, Cell, ErrorBar, ComposedChart, Area, Line,
} from 'recharts'

/**
 * Strategy Lab — select, construct, diagnose, rebalance.
 *
 * Four stages run in order because each consumes the one before: the posteriors feed the
 * optimiser's return view, the optimised weights are what gets diagnosed, and the
 * rebalance is measured against them. Each stage stops so the analyst can look before
 * continuing, and each exports, because this output is an input to a discussion.
 */

const th = 'px-3 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap'
const td = 'px-3 py-2 text-sm text-gray-800 whitespace-nowrap'
const inp = 'rounded-md border border-gray-300 px-2 py-1 text-sm'
const btn = 'flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50'
const btnPrimary = 'flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50'

const pct = (v: any, d = 1) => (v == null || Number.isNaN(v) ? '—' : `${(Number(v) * 100).toFixed(d)}%`)
const num = (v: any, d = 2) => (v == null || Number.isNaN(v) ? '—' : Number(v).toFixed(d))

const STAGES = ['Select', 'Construct', 'Diagnose', 'Rebalance', 'Allocate'] as const

export default function StrategyLab() {
  const [stage, setStage] = useState<string>('Select')

  // selection controls
  const [limit, setLimit] = useState(150)
  const [minLive, setMinLive] = useState(0.5)
  const [minBacktest, setMinBacktest] = useState(1.0)
  const [assetClass, setAssetClass] = useState('')
  const [riskPremia, setRiskPremia] = useState('')
  const [topN, setTopN] = useState(15)
  const [picked, setPicked] = useState<Record<string, boolean>>({})

  // construction controls
  const [cvarBudget, setCvarBudget] = useState(0.02)
  const [maxWeight, setMaxWeight] = useState(0.25)
  const [nDraws, setNDraws] = useState(150)
  const [tailConf, setTailConf] = useState(0.75)
  const [costBps, setCostBps] = useState(10)

  const [tiltSize, setTiltSize] = useState(0.25)

  const [status, setStatus] = useState('')

  const selectQ = useMutation({
    mutationFn: () => api.get('/strategy-lab/select', {
      params: {
        limit_tickers: limit, min_live_years: minLive, min_backtest_years: minBacktest,
        top_n: topN, asset_classes: assetClass || undefined,
        risk_premias: riskPremia || undefined,
      },
    }).then(r => r.data),
    onSuccess: (d) => {
      // Pre-tick everything returned: the ranking already applied the thresholds, so the
      // analyst is deselecting exceptions rather than rebuilding the list by hand.
      const next: Record<string, boolean> = {}
      ;(d.rows || []).forEach((r: any) => { next[r.name] = true })
      setPicked(next)
      setStatus(d.available ? `Scored ${d.n_scored}, excluded ${d.n_excluded}` : d.reason)
    },
    onError: (e: any) => setStatus(e?.response?.data?.detail || 'Selection failed'),
  })

  const rows: any[] = selectQ.data?.rows || []
  const chosen = rows.filter(r => picked[r.name])
  const muMap = Object.fromEntries(chosen.map(r => [r.name, r.posterior_mean]))
  const sdMap = Object.fromEntries(chosen.map(r => [r.name, r.posterior_sd]))

  const constructQ = useMutation({
    mutationFn: () => api.post('/strategy-lab/construct', {
      tickers: chosen.map(r => r.name), cvar_budget: cvarBudget, max_weight: maxWeight,
      n_draws: nDraws, expected_returns: muMap, return_sd: sdMap,
      tail_confidence: tailConf,
    }).then(r => r.data),
    onSuccess: (d) => setStatus(d.available ? `Held ${d.n_held} strategies` : d.reason),
    onError: (e: any) => setStatus(e?.response?.data?.detail || 'Construction failed'),
  })

  const weights = constructQ.data?.weights || {}
  const weightSd = constructQ.data?.resampled?.weight_sd || {}

  const diagnoseQ = useMutation({
    mutationFn: () => api.post('/strategy-lab/diagnose', {
      tickers: chosen.map(r => r.name), weights,
    }).then(r => r.data),
    onError: (e: any) => setStatus(e?.response?.data?.detail || 'Diagnostics failed'),
  })

  const rebalanceQ = useMutation({
    mutationFn: () => {
      const n = chosen.length || 1
      const equal = Object.fromEntries(chosen.map(r => [r.name, 1 / n]))
      return api.post('/strategy-lab/rebalance', {
        current: equal, target: weights, cost_bps: costBps,
        expected_returns: muMap, return_sd: sdMap,
      }).then(r => r.data)
    },
    onError: (e: any) => setStatus(e?.response?.data?.detail || 'Rebalance analysis failed'),
  })

  const allocateQ = useMutation({
    mutationFn: () => api.post('/strategy-lab/allocate', {
      tickers: chosen.map(r => r.name), weights, tilt: tiltSize,
      expected_returns: muMap, return_sd: sdMap,
    }).then(r => r.data),
    onSuccess: (d) => setStatus(d.available ? `Signals for ${d.rows?.length ?? 0} strategies` : d.reason),
    onError: (e: any) => setStatus(e?.response?.data?.detail || 'Allocation signals failed'),
  })

  const exportStage = async (name: string, payload: any, fmt: 'csv' | 'xlsx') => {
    if (!payload) return
    try {
      const res = await api.post('/strategy-lab/export',
        { stage: name, payload, fmt }, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `strategy-lab-${name}.${fmt}`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      setStatus('Export failed')
    }
  }

  const ExportButtons = ({ name, payload }: { name: string; payload: any }) => (
    <div className="flex gap-2">
      <button className={btn} disabled={!payload} onClick={() => exportStage(name, payload, 'csv')}>
        <Download size={14} />CSV
      </button>
      <button className={btn} disabled={!payload} onClick={() => exportStage(name, payload, 'xlsx')}>
        <Download size={14} />Excel
      </button>
    </div>
  )

  const done = {
    Select: rows.length > 0,
    Construct: !!constructQ.data?.available,
    Diagnose: !!diagnoseQ.data?.available,
    Rebalance: !!rebalanceQ.data?.available,
    Allocate: !!allocateQ.data?.available,
  } as Record<string, boolean>

  return (
    <div className="p-6">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Strategy Lab</h1>
          <p className="text-sm text-gray-500">
            Select on the probability a Sharpe is real, construct against a tail-risk budget,
            then check the diversification is actually there.
          </p>
        </div>
        {status && <div className="rounded bg-gray-100 px-3 py-1.5 text-xs text-gray-600">{status}</div>}
      </div>

      {/* stage rail */}
      <div className="mb-5 flex flex-wrap items-center gap-1 border-b border-gray-200">
        {STAGES.map((s, i) => (
          <div key={s} className="flex items-center">
            <button
              onClick={() => setStage(s)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm ${
                stage === s ? 'border-b-2 border-blue-600 font-medium text-blue-700' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {done[s] && <CheckCircle2 size={14} className="text-emerald-600" />}
              <span className="text-xs text-gray-400">{i + 1}</span>{s}
            </button>
            {i < STAGES.length - 1 && <ChevronRight size={14} className="text-gray-300" />}
          </div>
        ))}
      </div>

      {/* ---------------------------------------------------------------- select */}
      {stage === 'Select' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
            <label className="text-sm">Universe size <input className={`${inp} ml-1 w-20`} type="number" value={limit} onChange={e => setLimit(+e.target.value)} /></label>
            <label className="text-sm">Min live yrs <input className={`${inp} ml-1 w-16`} type="number" step="0.5" value={minLive} onChange={e => setMinLive(+e.target.value)} /></label>
            <label className="text-sm">Min backtest yrs <input className={`${inp} ml-1 w-16`} type="number" step="0.5" value={minBacktest} onChange={e => setMinBacktest(+e.target.value)} /></label>
            <label className="text-sm">Keep top <input className={`${inp} ml-1 w-16`} type="number" value={topN} onChange={e => setTopN(+e.target.value)} /></label>
            <input className={`${inp} w-36`} placeholder="asset class" value={assetClass} onChange={e => setAssetClass(e.target.value)} />
            <input className={`${inp} w-36`} placeholder="risk premia" value={riskPremia} onChange={e => setRiskPremia(e.target.value)} />
            <button className={btnPrimary} disabled={selectQ.isPending} onClick={() => selectQ.mutate()}>
              {selectQ.isPending ? <Loader2 className="animate-spin" size={15} /> : <Play size={15} />}Rank universe
            </button>
            <ExportButtons name="select" payload={selectQ.data} />
          </div>

          {selectQ.isPending && <Spinner label="Estimating posterior Sharpe distributions…" />}
          {selectQ.data && !selectQ.data.available && (
            <Note tone="warn">{selectQ.data.reason}</Note>
          )}

          {rows.length > 0 && (
            <>
              <Note>
                {selectQ.data.method} {selectQ.data.n_excluded > 0 &&
                  `${selectQ.data.n_excluded} strategies lacked enough live or backtest history and were excluded.`}
              </Note>

              {/* Which deflator produced these numbers belongs next to them. A flat
                  haircut is the naive benchmark, so if that is what ran, say so here
                  rather than leaving it to be inferred. */}
              {selectQ.data.deflator_note && (
                <Note tone={selectQ.data.deflator?.method === 'flat' ? 'warn' : undefined}>
                  {selectQ.data.deflator_note}
                </Note>
              )}

              {selectQ.data.information_time_note && (
                <Note>{selectQ.data.information_time_note}</Note>
              )}

              <Panel title="Backtest Sharpe versus what the evidence supports">
                <ResponsiveContainer width="100%" height={300}>
                  <ScatterChart margin={{ top: 10, right: 20, bottom: 30, left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" dataKey="bt" name="backtest" tick={{ fontSize: 11 }}
                           label={{ value: 'backtest Sharpe', position: 'insideBottom', offset: -15, fontSize: 11 }} />
                    <YAxis type="number" dataKey="post" name="posterior" tick={{ fontSize: 11 }}
                           label={{ value: 'posterior mean', angle: -90, position: 'insideLeft', fontSize: 11 }} />
                    <ZAxis range={[45, 45]} />
                    <Tooltip formatter={(v: any) => num(v, 3)} />
                    <Scatter data={rows.map(r => ({
                      bt: r.backtest_sharpe, post: r.posterior_mean, name: r.name, p: r.p_sharpe_above_0,
                    }))}>
                      {rows.map((r, i) => (
                        <Cell key={i} fill={r.p_sharpe_above_0 > 0.9 ? '#2F6B4F' : r.p_sharpe_above_0 > 0.7 ? '#8A6A1F' : '#B8BFC6'} />
                      ))}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
                <div className="mt-1 text-xs text-gray-500">
                  Points sit well below the diagonal: a backtest is discounted before it counts.
                  Green marks P(Sharpe&nbsp;&gt;&nbsp;0) above 90%.
                </div>
              </Panel>

              <Panel title={`Ranked by P(Sharpe > 0) — ${chosen.length} of ${rows.length} selected`}>
                <div className="max-h-[26rem] overflow-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="sticky top-0 bg-white">
                      <tr>
                        <th className={th}>
                          <input type="checkbox" checked={chosen.length === rows.length}
                                 onChange={e => setPicked(Object.fromEntries(rows.map(r => [r.name, e.target.checked])))} />
                        </th>
                        {['Strategy', 'Asset class', 'Backtest SR', 'Haircut', 'Evidence yrs', 'Live SR', 'Posterior', '±SD', 'P(genuine)', 'P(SR>0)'].map(h => <th key={h} className={th}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {rows.map((r, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className={td}>
                            <input type="checkbox" checked={!!picked[r.name]}
                                   onChange={e => setPicked({ ...picked, [r.name]: e.target.checked })} />
                          </td>
                          <td className={td}>{r.name}</td>
                          <td className={td}>{r.asset_class || '—'}</td>
                          <td className={td}>{num(r.backtest_sharpe)}</td>
                          {/* what survived the deflator, per strategy, not a blanket 50% */}
                          <td className={td} title={r.retention_sd != null ? `±${num(r.retention_sd, 2)}` : undefined}>
                            {r.retention != null ? pct(r.retention, 0) : '—'}
                          </td>
                          {/* information years vs calendar years: a long record in one
                              regime is worth less than its length suggests */}
                          <td className={td} title={r.backtest_years != null ? `${num(r.backtest_years, 1)} calendar yrs` : undefined}>
                            {r.information_years != null
                              ? `${num(r.information_years, 1)} / ${num(r.backtest_years, 1)}`
                              : '—'}
                          </td>
                          <td className={td}>{num(r.live_sharpe)}</td>
                          <td className={`${td} font-medium`}>{num(r.posterior_mean, 3)}</td>
                          <td className={td}>{num(r.posterior_sd, 3)}</td>
                          <td className={td}>{num(r.p_genuine, 2)}</td>
                          <td className={td}>{num(r.p_sharpe_above_0, 3)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* ------------------------------------------------------------- construct */}
      {stage === 'Construct' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
            <label className="text-sm">CVaR budget <input className={`${inp} ml-1 w-20`} type="number" step="0.005" value={cvarBudget} onChange={e => setCvarBudget(+e.target.value)} /></label>
            <label className="text-sm">Max weight <input className={`${inp} ml-1 w-20`} type="number" step="0.05" value={maxWeight} onChange={e => setMaxWeight(+e.target.value)} /></label>
            <label className="text-sm">Resample draws <input className={`${inp} ml-1 w-20`} type="number" value={nDraws} onChange={e => setNDraws(+e.target.value)} /></label>
            <label className="text-sm" title="Quantile of each strategy's own CVaR distribution at which it enters the optimiser. 0.5 makes an uncertain tail free; higher penalises it.">Tail confidence <input className={`${inp} ml-1 w-20`} type="number" step="0.05" min="0.05" max="0.95" value={tailConf} onChange={e => setTailConf(+e.target.value)} /></label>
            <button className={btnPrimary} disabled={chosen.length < 2 || constructQ.isPending} onClick={() => constructQ.mutate()}>
              {constructQ.isPending ? <Loader2 className="animate-spin" size={15} /> : <Play size={15} />}Optimise
            </button>
            <ExportButtons name="construct" payload={constructQ.data} />
          </div>

          {chosen.length < 2 && <Note tone="warn">Select at least two strategies on the previous stage.</Note>}
          {constructQ.isPending && <Spinner label="Solving the CVaR-budget programme and resampling…" />}
          {constructQ.data && !constructQ.data.available && <Note tone="warn">{constructQ.data.reason}</Note>}
          {constructQ.data?.tail_note && <Note>{constructQ.data.tail_note}</Note>}
          {constructQ.data?.budget_slack_note && <Note tone="warn">{constructQ.data.budget_slack_note}</Note>}

          {constructQ.data?.available && (
            <>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Kpi label="Strategies held" value={constructQ.data.n_held} />
                <Kpi label="CVaR budget" value={pct(cvarBudget, 2)} />
                <Kpi label="Tightest reachable" value={pct(constructQ.data.min_achievable_cvar, 2)} />
                <Kpi label="Observations" value={constructQ.data.window?.n_obs} />
              </div>

              {constructQ.data.expected_return_source?.includes('historical') && (
                <Note tone="warn">Returns came from historical means — {constructQ.data.expected_return_source}</Note>
              )}

              <Panel title="Weights, with the spread across resampled solves">
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={Object.entries(weights)
                    .filter(([, w]) => (w as number) > 1e-4)
                    .sort((a, b) => (b[1] as number) - (a[1] as number))
                    .map(([t, w]) => ({ name: t.replace(' Index', ''), weight: w, sd: weightSd[t] || 0 }))}
                    margin={{ top: 10, right: 20, bottom: 60, left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-40} textAnchor="end" height={70} tick={{ fontSize: 10 }} />
                    <YAxis tickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v: any) => pct(v, 2)} />
                    <Bar dataKey="weight" fill="#2563eb">
                      <ErrorBar dataKey="sd" width={4} strokeWidth={1.5} stroke="#A63D33" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-1 text-xs text-gray-500">
                  Red bars are the standard deviation of each weight across {constructQ.data.resampled?.n_success ?? 0} resampled
                  solves. Where the bar rivals the weight, that position is estimation noise rather than a conclusion.
                </div>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* -------------------------------------------------------------- diagnose */}
      {stage === 'Diagnose' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
            <button className={btnPrimary} disabled={chosen.length < 2 || diagnoseQ.isPending} onClick={() => diagnoseQ.mutate()}>
              {diagnoseQ.isPending ? <Loader2 className="animate-spin" size={15} /> : <Play size={15} />}Run diagnostics
            </button>
            <ExportButtons name="diagnose" payload={diagnoseQ.data} />
          </div>

          {diagnoseQ.isPending && <Spinner label="Measuring absorption, bets, turbulence and agreement…" />}
          {diagnoseQ.data?.available && (
            <>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Kpi label="Absorption ratio" value={num(diagnoseQ.data.absorption_ratio, 3)} />
                <Kpi label="Effective bets" value={`${num(diagnoseQ.data.effective_number_of_bets, 2)} of ${diagnoseQ.data.n_strategies}`} />
                <Kpi label="Avg pairwise corr" value={num(diagnoseQ.data.avg_pairwise_corr, 3)} />
                <Kpi label="Turbulence pctile" value={pct(diagnoseQ.data.turbulence_percentile, 0)} />
              </div>

              {diagnoseQ.data.flags?.length > 0 ? (
                <Note tone="warn">
                  <b>Worth a look:</b> {diagnoseQ.data.flags.join(' · ')}
                </Note>
              ) : (
                <Note>No diagnostic is unusual against its own history.</Note>
              )}

              <Note>
                Absorption and effective bets answer different questions. Absorption says how
                many independent drivers there are; effective bets says how spread the capital
                is. High absorption <i>with</i> a high bet count is the combination that looks
                diversified and is not.
              </Note>


              {(diagnoseQ.data.drawdown_overlap || []).length > 0 && (
                <Panel title="Underwater together — co-movement the correlation matrix misses">
                  <Note>{diagnoseQ.data.drawdown_overlap_note}</Note>
                  <div className="mt-3 max-h-72 overflow-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="sticky top-0 bg-white">
                        <tr>{['Pair', 'R*dd', 'Return corr', 'Gap', 'Depth corr'].map(h => <th key={h} className={th}>{h}</th>)}</tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {diagnoseQ.data.drawdown_overlap.map((r: any, i: number) => (
                          <tr key={i}>
                            <td className={td}>{r.a} / {r.b}</td>
                            <td className={td}>{num(r.r_star_dd, 2)}</td>
                            <td className={td}>{num(r.return_corr, 2)}</td>
                            <td className={`${td} font-medium`}>{num(r.gap, 2)}</td>
                            <td className={td}>{num(r.depth_corr, 2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Panel>
              )}

              {(diagnoseQ.data.tail_dependence || []).length > 0 && (
                <Panel title="Crashing together — dependence a covariance matrix assumes away">
                  <Note>{diagnoseQ.data.tail_dependence_note}</Note>
                  <div className="mt-3 max-h-72 overflow-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="sticky top-0 bg-white">
                        <tr>{['Pair', 'Lower tail', 'Upper tail', 'Gaussian', 'Excess', 'Asymmetry'].map(h => <th key={h} className={th}>{h}</th>)}</tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {diagnoseQ.data.tail_dependence.map((r: any, i: number) => (
                          <tr key={i}>
                            <td className={td}>{r.a} / {r.b}</td>
                            <td className={td}>{num(r.lower, 2)}</td>
                            <td className={td}>{num(r.upper, 2)}</td>
                            <td className={td}>{num(r.gaussian, 2)}</td>
                            <td className={`${td} font-medium`}>{num(r.excess_over_gaussian, 2)}</td>
                            <td className={td}>{num(r.asymmetry, 2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Panel>
              )}

              {diagnoseQ.data.constraints && (
                <Panel title="What the position cap did">
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <Kpi label="Names on the cap" value={diagnoseQ.data.constraints.n_binding} />
                    <Kpi label="Weight at the cap" value={pct(diagnoseQ.data.constraints.weight_at_cap, 0)} />
                    <Kpi label="TE vs risk parity" value={pct(diagnoseQ.data.constraints.te_vs_risk_parity, 1)} />
                    <Kpi label="TE vs equal weight" value={pct(diagnoseQ.data.constraints.te_vs_equal_weight, 1)} />
                  </div>
                  {diagnoseQ.data.constraints.constraint_driven && (
                    <Note tone="warn">
                      More than a third of the book sits on the position cap, so this
                      allocation is substantially the cap's opinion rather than the model's.
                    </Note>
                  )}
                </Panel>
              )}
              {diagnoseQ.data.dispersion_series?.length > 0 && (
                <Panel title="How much the correlation estimate depends on the model chosen">
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={diagnoseQ.data.dispersion_series.map((d: any) => ({
                      date: d.date, spread: (d.max ?? 0) - (d.min ?? 0), mean: d.mean,
                    }))} margin={{ top: 10, right: 20, bottom: 30, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 9 }} interval={Math.ceil((diagnoseQ.data.dispersion_series.length || 1) / 8)} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v: any) => num(v, 3)} />
                      <Bar dataKey="spread" fill="#8A6A1F" />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="mt-1 text-xs text-gray-500">
                    Spread between the highest and lowest average correlation across five estimators
                    and three windows. A wide bar means the risk number is substantially a modelling
                    choice rather than a measurement.
                  </div>
                </Panel>
              )}
            </>
          )}
        </div>
      )}

      {/* ------------------------------------------------------------- rebalance */}
      {stage === 'Rebalance' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
            <label className="text-sm">Cost (bps) <input className={`${inp} ml-1 w-20`} type="number" value={costBps} onChange={e => setCostBps(+e.target.value)} /></label>
            <button className={btnPrimary} disabled={!constructQ.data?.available || rebalanceQ.isPending} onClick={() => rebalanceQ.mutate()}>
              {rebalanceQ.isPending ? <Loader2 className="animate-spin" size={15} /> : <Play size={15} />}Analyse trade
            </button>
            <ExportButtons name="rebalance" payload={rebalanceQ.data} />
          </div>

          <Note>Measured from an equal-weight book of the selected strategies to the optimised weights.</Note>
          {rebalanceQ.isPending && <Spinner label="Pricing the trade against its estimated benefit…" />}

          {rebalanceQ.data?.available && (
            <>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Kpi label="Turnover" value={pct(rebalanceQ.data.turnover, 1)} />
                <Kpi label="Cost" value={pct(rebalanceQ.data.cost, 3)} />
                <Kpi label="Break-even" value={`${num(rebalanceQ.data.breakeven_years, 2)} yrs`} />
                <Kpi label="Pays off in 1y" value={pct(rebalanceQ.data.prob_pays_off_1y, 0)} />
              </div>

              <Note tone={rebalanceQ.data.verdict?.startsWith('Do not') ? 'warn' : 'ok'}>
                {rebalanceQ.data.verdict}
              </Note>
              {rebalanceQ.data.assumes_certainty && (
                <Note tone="warn">
                  No standard errors were supplied, so the probability above assumes the return
                  estimates are exact.
                </Note>
              )}

              <Panel title="How much of the benefit a partial trade captures">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={(rebalanceQ.data.path || []).map((p: any) => ({
                    fraction: `${Math.round(p.fraction * 100)}%`,
                    net: p.net_benefit_1y, cost: p.cost,
                  }))} margin={{ top: 10, right: 20, bottom: 30, left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="fraction" tick={{ fontSize: 10 }} interval={1} />
                    <YAxis tickFormatter={(v: number) => `${(v * 100).toFixed(1)}%`} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v: any) => pct(v, 3)} />
                    <Bar dataKey="net" fill="#2F6B4F" />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-1 text-xs text-gray-500">
                  Net one-year benefit after cost, by how far the trade goes. Benefit is concave and
                  cost is linear, so the last stretch is usually the expensive part.
                </div>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* -------------------------------------------------------------- allocate */}
      {stage === 'Allocate' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
            <label className="text-sm" title="Share of the base position a signal may move. Sized from measured information coefficients, not conviction.">
              Tilt size <input className={`${inp} ml-1 w-20`} type="number" step="0.05" min="0" max="1" value={tiltSize} onChange={e => setTiltSize(+e.target.value)} />
            </label>
            <button className={btnPrimary} disabled={chosen.length < 2 || allocateQ.isPending} onClick={() => allocateQ.mutate()}>
              {allocateQ.isPending ? <Loader2 className="animate-spin" size={15} /> : <Play size={15} />}Read signals
            </button>
            <ExportButtons name="allocate" payload={allocateQ.data} />
          </div>

          {allocateQ.isPending && <Spinner label="Computing value, carry, trend and crowding…" />}
          {allocateQ.data && !allocateQ.data.available && <Note tone="warn">{allocateQ.data.reason}</Note>}

          {allocateQ.data?.available && (
            <>
              <Note>{allocateQ.data.note}</Note>

              <Panel title="Timing signals and the tilt they justify">
                <div className="max-h-[26rem] overflow-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="sticky top-0 bg-white">
                      <tr>{['Strategy', 'Value', 'Carry', 'Trend', 'Crowding', 'Composite', 'Posterior', 'Tilted', 'Shift (sd)'].map(h => <th key={h} className={th}>{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(allocateQ.data.rows || []).map((r: any, i: number) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className={td}>{r.name}</td>
                          <td className={td}>{num(r.value)}</td>
                          <td className={td}>{num(r.carry)}</td>
                          <td className={td}>{num(r.trend)}</td>
                          <td className={td}>{num(r.crowding)}</td>
                          <td className={`${td} font-medium`}>{num(r.composite)}</td>
                          <td className={td}>{num(r.posterior_mean, 3)}</td>
                          <td className={td}>{num(r.tilted_mean, 3)}</td>
                          <td className={td} title={r.capped ? 'capped at one posterior standard deviation' : undefined}>
                            {num(r.shift_in_sd, 2)}{r.capped ? ' (capped)' : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  All signals are z-scores against each strategy's own history. A positive
                  composite is cheap, carrying well, trending and uncrowded.
                </div>
              </Panel>

              {(allocateQ.data.decay_cones || []).length > 0 && (
                <Panel title="Alpha decay against a discounted backtest">
                  <Note>{allocateQ.data.decay_note}</Note>
                  <div className="mt-3 grid gap-4 lg:grid-cols-2">
                    {allocateQ.data.decay_cones.map((c: any) => (
                      <div key={c.name} className="rounded border border-gray-200 p-3">
                        <div className="mb-1 flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-800">{c.name}</span>
                          <span className={`rounded px-2 py-0.5 text-xs ${c.below_cone ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                            {c.below_cone ? 'below the cone' : 'inside the cone'}
                          </span>
                        </div>
                        <ResponsiveContainer width="100%" height={180}>
                          <ComposedChart data={c.path} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="i" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v: any) => `${(v * 100).toFixed(0)}%`} />
                            <Tooltip formatter={(v: any) => pct(v, 1)} />
                            <Area type="monotone" dataKey="p95" stroke="none" fill="#D8E3EE" />
                            <Area type="monotone" dataKey="p05" stroke="none" fill="#FFFFFF" />
                            <Line type="monotone" dataKey="p50" stroke="#8A9BA8" dot={false} strokeDasharray="4 3" />
                            <Line type="monotone" dataKey="live" stroke="#1F4E79" dot={false} strokeWidth={2} />
                          </ComposedChart>
                        </ResponsiveContainer>
                        <div className="mt-1 text-xs text-gray-500">{c.verdict}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    Shaded band is the 5th-to-95th percentile of bootstrapped paths from the
                    haircut backtest; the dark line is what actually happened.
                  </div>
                </Panel>
              )}

              {allocateQ.data.regime && (
                <Panel title="What followed backdrops like this one">
                  <Note tone={allocateQ.data.regime.analogs?.has_precedent ? 'info' : 'warn'}>
                    {allocateQ.data.regime.verdict}
                  </Note>
                  {allocateQ.data.regime.analogs?.has_precedent && (allocateQ.data.regime.outcomes || []).length > 0 && (
                    <div className="mt-3 max-h-72 overflow-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="sticky top-0 bg-white">
                          <tr>{['Strategy', 'Analogs', 'Median', 'q25', 'q75', 'Hit rate', 'Worst'].map(h => <th key={h} className={th}>{h}</th>)}</tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {allocateQ.data.regime.outcomes.map((o: any, i: number) => (
                            <tr key={i}>
                              <td className={td}>{o.name}</td>
                              <td className={td}>{o.n}</td>
                              <td className={`${td} font-medium`}>{pct(o.median, 2)}</td>
                              <td className={td}>{pct(o.q25, 2)}</td>
                              <td className={td}>{pct(o.q75, 2)}</td>
                              <td className={td}>{pct(o.hit_rate, 0)}</td>
                              <td className={td}>{pct(o.worst, 2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Panel>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

function Kpi({ label, value }: { label: string; value: any }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="mt-0.5 text-xl font-semibold text-gray-900">{value ?? '—'}</div>
    </div>
  )
}

function Panel({ title, children }: { title: string; children: any }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-2 text-sm font-medium text-gray-700">{title}</div>
      {children}

    </div>
  )
}

function Spinner({ label }: { label?: string }) {
  return <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="animate-spin" size={16} />{label || 'Loading…'}</div>
}

function Note({ children, tone = 'info' }: { children: any; tone?: 'info' | 'warn' | 'ok' }) {
  const cls = tone === 'warn'
    ? 'bg-amber-50 text-amber-800 border-amber-200'
    : tone === 'ok'
      ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
      : 'bg-blue-50 text-blue-800 border-blue-200'
  return (
    <div className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${cls}`}>
      {tone === 'warn' && <AlertTriangle size={14} className="mt-0.5 flex-none" />}
      <div>{children}</div>
    </div>
  )
}
